import React, { useEffect, useState, useMemo, useRef } from 'react';
import { CircularProgress, Button, Checkbox, Box, Typography } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Text from '../text';
import PrimaryButton from '../button/PrimaryButton';
import SecondaryButton from '../button/SecondaryButton';
import ApiService from 'utils/services/ApiService';
import {
	saveTestResults,
	getTestResults,
	saveSyncCache
} from '../../utils/services/idbService';
import { useDispatch } from 'react-redux';
import { updateSingleSitesData } from 'utils/redux/reducers/allSitesLoadedReducer';
import CONSTANTS from '../../utils/constants/UIConstants';

interface TableColumnProps {
	siteId: number;
	onClose: () => void;
  isOnline: boolean
}

interface ColumnData {
	id: number;
	colName: string;
	colType: string;
	desktop: boolean;
	mobile: boolean;
	both: boolean;
	siteId: number;
	userId: number;
}

interface TypedColumnData {
	type: string;
	cols: ColumnData[];
}

const TableColumns: React.FC<TableColumnProps> = ({ siteId, onClose, isOnline }) => {
	const [columnsData, setColumnsData] = useState<ColumnData[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const dispatch = useDispatch();
	const fetchTableColumns = async () => {
		setIsLoading(true);
		if (!isOnline) {
			const cachedData = await getTestResults(
				`allSites`,
				localStorage.getItem('currentUser') ?? 'unknown'
			);
			const index = cachedData.findIndex((site: any) => site.id === siteId);
			const transformedData = cachedData[index]?.tableColumnSettingsAllVOs.map((column: any) => ({
				...column,
				both: (column.desktop && column.mobile) || column.visible,
				siteId: cachedData[index].id,
			}));
			setColumnsData(transformedData || []);
			setIsLoading(false);
			return;
		}
		ApiService.getTableColumns(siteId)
			.then((response) => {
				const transformedData = response.data.map(
					(column: { desktop: any; mobile: any }) => ({
						...column,
						both: column.desktop && column.mobile,
					})
				);
				setColumnsData(transformedData);
			})
			.catch((error) => {
				console.error('Error fetching table columns:', error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	useEffect(() => {
		fetchTableColumns();
	}, [siteId]);
	const originalColumnsData = useRef<typeof columnsData>([]);

	useEffect(() => {
	if (originalColumnsData.current.length === 0 && columnsData.length > 0) {
		originalColumnsData.current = structuredClone(columnsData);
	}
	}, [columnsData]);

	const columnsByType = columnsData.reduce((acc, column) => {
		acc[column.colType] = acc[column.colType] || [];
		acc[column.colType].push(column);
		return acc;
	}, {} as Record<string, ColumnData[]>);

	const sortedColumnsByType = useMemo(() => {
		return (
			[
				{ type: "Sample", cols: columnsByType.Sample || []},
				{ type: "Inputs", cols: columnsByType.Input || []},
				{ type: "Results", cols: columnsByType.Output || []},
			]
		);
	}, [columnsByType]);

	const handleToggle = (
		columnId: number,
		colName: string,
		key: 'desktop' | 'mobile' | 'both'
	) => {
		setColumnsData((prev) =>
			prev.map((column) => {
				if (column.id === columnId && column.colName === colName) {
					const updatedColumn = {
						...column,
						[key]: !column[key],
					};
					if (key !== 'both') {
						updatedColumn.both =
							updatedColumn.desktop && updatedColumn.mobile;
					} else {
						updatedColumn.desktop = updatedColumn.both;
						updatedColumn.mobile = updatedColumn.both;
					}
					return updatedColumn;
				}
				return column;
			})
		);
	};

	const handleSave = async () => {
		setIsLoading(true);

		

		const requestBodySync = columnsData.map(
			({ id, colName, colType, desktop, mobile, siteId, userId, both }) => {
				// Include 'id' only if it is not undefined
				return {
					...(typeof id !== 'undefined' && { id }),
					colName,
					colType,
					siteId,
					desktop,
					mobile,
					userId,
					visible: both,
				};
			}
		);
		const requestBodyAPI = columnsData.map(
			({ id, colName, colType, desktop, mobile, siteId, userId }) => {
				// Include 'id' only if it is not undefined
				return {
					...(typeof id !== 'undefined' && { id }),
					colName,
					colType,
					siteId,
					desktop,
					mobile,
					userId, //visible field isn't accepted on the API.
				};
			}
		);

		//Update the table columns cache first
		const username = localStorage.getItem('currentUser');
		if (username) {
			await saveTestResults(
				`columns_data_${siteId}`,
				requestBodySync,
				username
			);
		}
		if (!isOnline) {
			try {
				const cachedData = await getTestResults(
					`allSites`,
					localStorage.getItem('currentUser') ?? 'unknown'
				);
				const groupsCache = await getTestResults(
					`groupsInfo`,
					localStorage.getItem('currentUser') ?? 'unknown'
				  );
				
				const siteGroup = groupsCache.find((group: any) => group.sites.some((site: any) => site.id === siteId));
				const index = cachedData.findIndex((site: any) => site.id === siteId);
				const site = {...cachedData[index]};
				site.tableColumnSettingsAllVOs = requestBodySync;
				cachedData[index] = site;
				await saveTestResults(`allSites`, cachedData, localStorage.getItem('currentUser') ?? 'unknown');
				await saveSyncCache({...site, groupFK: siteGroup.id.toString(), action: "UPDATE"}, "siteSyncCache");
				dispatch(updateSingleSitesData(requestBodySync));
				onClose();
			} catch (error) {
				console.error('Error updating cache:', error);
			}
			return;
		}

		const columnDiffs = columnsData
		.filter((col) => {
			const original = originalColumnsData.current.find((o) => o.id === col.id);
			return original && (
				original.both !== col.both ||
				original.desktop !== col.desktop ||
				original.mobile !== col.mobile
			);
		})
		.map((col) => {
			const original = originalColumnsData.current.find((o) => o.id === col.id)!;
			return {
				id: col.id,
				colName: col.colName,
				...(original.both !== col.both && { both: col.both }),
				...(original.desktop !== col.desktop && { desktop: col.desktop }),
				...(original.mobile !== col.mobile && { mobile: col.mobile }),
			};
		});
		const formatColumnDiffs = (diffs: typeof columnDiffs) => {
			if (diffs.length === 0) return 'No columns changed';
			
			return 'Updated columns: ' + diffs
				.map((col) => `${col.colName}: ${col.both ? 'visible' : 'hidden'}`)
				.join(', ');
		};
		
		ApiService.postCreateUpdateTableColumns(requestBodyAPI, siteId, formatColumnDiffs(columnDiffs))
			.then((response) => {
				// Handle successful update response
				fetchTableColumns();
				dispatch(updateSingleSitesData(response.data))
				onClose();
        // window.location.reload();
			})
			.catch((error) => {
				// Handle any errors
				console.error('Error updating table columns:', error);
			})
			.finally(() => {
				setIsLoading(false); // Set loading to false
			});
	};

	return (
		<div>
			<Text id="table-columns-header" addStyle="!text-[#2C2C2C] !font-inter !text-[20px] !mt-[24px] !font-semibold !mb-[10px]]">
				Table Columns
			</Text>
			<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px] !mt-[24px]">
				Adjust which columns are displayed in the summary view of the
				table. Available columns are based on the test kits currently
				used at this site. Hiding columns will not delete any data, and
				each test's full set of related data will still be visible when
				the row is expanded.
			</Text>

			{isLoading ? (
				<div className="flex justify-center items-center h-[200px]">
					<CircularProgress />
				</div>
			) : (
				<div className="mt-4">
					<Box
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: '16px',
							letterSpacing: '0.15px',
							lineHeight: '24px',
							color: '#666666',
							pb: 1,
						}}
					>
						{/* Header - Temporarily removed for PWA-369 */}
						{/* <div className="grid grid-cols-8 !text-[#556670] !font-inter !text-[14px]">
							<div className="col-span-3">Columns</div>

							<>
								<div className="col-span-3 flex ">
									{<div className="mr-6">Desktop Only </div>{' '}
									<div className="mr-[54px]">Mobile Only</div>{' '}}
									<div>Both</div>
								</div>
							</>
						</div> */}

						{!isLoading &&
							sortedColumnsByType.map(
								(index) => (
									<Box
										key={index.type}
										sx={{
											borderBottom: 1,
											borderColor: CONSTANTS.BORDER_COLOR,
										}}
									>
										<Text addStyle="!font-jost !text-[16px] !font-semibold !my-6 !text-[#2C2C2C]">
											{index.type}
										</Text>
										{index.cols.map((column) => (
											<div
												key={column.id}
												className="grid grid-cols-10 py-2"
											>
												<div className="col-span-4 flex items-center justify-start !text-[#556670] !font-jost !text-[16px] !font-normal">
													{column.colName}
												</div>
												{/* Temporarily removed for PWA-369 <div className="col-span-1">
													<FormControlLabel
														control={
															<Checkbox
																checked={
																	column.desktop
																}
																onChange={() =>
																	handleToggle(
																		column.id,
																		column.colName,
																		'desktop'
																	)
																}
															/>
														}
														label=""
													/>
												</div>
												<div className="col-span-1">
													<FormControlLabel
														control={
															<Checkbox
																checked={
																	column.mobile
																}
																onChange={() =>
																	handleToggle(
																		column.id,
																		column.colName,
																		'mobile'
																	)
																}
															/>
														}
														label=""
													/>
												</div> */}
												<div className="col-span-1">
													<FormControlLabel
														control={
															<Checkbox
																checked={
																	column.both
																}
																onChange={() =>
																	handleToggle(
																		column.id,
																		column.colName,
																		'both'
																	)
																}
															/>
														}
														label=""
													/>
												</div>
											</div>
										))}
									</Box>
								)
							)}
					</Box>
					<div className="flex justify-end space-x-1 mt-4">
						<Button
							color="primary"
							className="!text-[#9EACB4]"
							onClick={onClose}
						>
							CANCEL
						</Button>
						{isLoading ? (
							<CircularProgress />
						) : (
							<PrimaryButton onClick={handleSave}>
								SAVE
							</PrimaryButton>
						)}
					</div>
					<div className="">{/* Rows */}</div>
				</div>
			)}
		</div>
	);
};

export default TableColumns;
